import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreDialog } from '@ingenico/react-ui/core-dialog';
import { CoreIconButton } from '@ingenico/react-ui/core-icon-button';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Z_INDEX_PARAMETER_MODULE_DIALOG } from '@ingenico/react-form-builder';
import ReplayIcon from '@mui/icons-material/Replay';
import type { ReactElement } from 'react';
import styles from './EditResourceModal.module.scss';
import { LNGS_NAMESPACE_RESOURCES } from '@ingenico/util-i18n-config';
import { useTranslation } from 'react-i18next';
import { Banner } from '@ingenico/react-ui/banner';
import { useEditResourceModalVM } from './useEditResourceModal.vm';
import type { ResourceListItemFront } from '../../../domain/domain/models/resources.model';
import { EditResourceForm } from '../edit-resource-form/EditResourceForm.ctrl';

export interface EditResourceModalProps {
  readonly onClose?: () => void;
  readonly onConfirm?: (resource: ResourceListItemFront) => void;
  readonly isLoading?: boolean;
  readonly isOpen: boolean;
  readonly errorMessage?: string;
  readonly resource?: ResourceListItemFront;
}

export function EditResourceModal({
  onClose,
  onConfirm,
  isLoading,
  isOpen,
  errorMessage,
  resource,
}: EditResourceModalProps): ReactElement {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_RESOURCES));
  const { form, handleConfirm, hasNoChanges } = useEditResourceModalVM({ resource, onConfirm });
  const hasError = !!errorMessage;
  return (
    <CoreDialog
      onClose={onClose}
      classes={{
        paper: styles.paper,
        root: styles.basicDialogModal,
      }}
      fullWidth
      maxWidth={'sm'}
      open={isOpen}
      slotProps={{ transition: { onExited: onClose } }}
      sx={{ zIndex: Z_INDEX_PARAMETER_MODULE_DIALOG }}
      disableEnforceFocus
    >
      <DialogTitle className={styles.header}>
        <div className={styles.dialogTitle}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>
            {t('modal.editResource.title', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT })}
          </CoreTypography>
        </div>
        <CoreIconButton onClick={onClose}>
          <CloseIcon />
        </CoreIconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {!isLoading && errorMessage && <Banner message={errorMessage} />}
        <EditResourceForm form={form} />
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <div className={styles.action}>
          <CoreButton onClick={onClose} variant={'text'} disabled={isLoading}>
            {t('button.cancel', { ns: LNGS_NAMESPACE_RESOURCES.COMMON })}
          </CoreButton>
          <CoreButton
            startIcon={hasError ? <ReplayIcon /> : undefined}
            onClick={handleConfirm}
            loading={isLoading}
            variant="contained"
            disabled={isLoading || hasNoChanges}
          >
            {hasError
              ? t('button.retry', { ns: LNGS_NAMESPACE_RESOURCES.COMMON })
              : t('button.save', { ns: LNGS_NAMESPACE_RESOURCES.COMMON })}
          </CoreButton>
        </div>
      </DialogActions>
    </CoreDialog>
  );
}
